import React from 'react';
import { ArrowRight, FileBarChart, Boxes, Gauge, Activity, Lock } from 'lucide-react';
import { RESEARCH_TUTORIAL_STEPS } from '../process/ResearchWelcomePanel';
import { BUILD_TUTORIAL_STEPS } from '../process/BuildWelcomePanel';
import ModuleTutorialCard from './ModuleTutorialCard';
import { useShellUiActions } from '../../context/ShellUiContext';
import { useAuthActions } from '../../context/AuthContext';
import { isMcpOnlyCustomer } from '../../utils/moduleAccess';

// BAR-538 phase 1: the start page becomes a stack of module cards under the
// fixed composer. Research and Build ship first, in hardcoded order; the
// Customize control (hide/reorder + per-user persistence) and the remaining
// cards (Reporting, Regulatory monitoring, Inventory, Limit monitoring)
// follow in later phases — see docs/plans/2026-07-12-editable-start-page.md.
//
// Card content = the workspace welcome tutorials as a STATIC step list
// (hover/click selects the visual; no self-running animation — decided
// with Kawi 2026-07-12 after the auto-carousel felt too busy). The steps
// come from the panels' exported arrays, so workspace tutorial and
// start-page card stay in sync.
// Header action-link style shared by every module card header.
const openLinkBase =
    'group font-mono text-[11px] uppercase tracking-[0.08em] font-semibold transition-colors flex items-center gap-1.5';
const openLinkClass = `${openLinkBase} text-fg hover:underline underline-offset-2`;
const lockedLinkClass = `${openLinkBase} text-fg-tertiary hover:text-fg-secondary`;

// "Soon" mark — same style constant as the sidebar's disabled entries
// (App.tsx navSoonBadge), reused verbatim so the two read as one system.
const soonBadgeClass =
    'text-[9px] font-semibold bg-bg-warm text-fg-tertiary px-1 py-px rounded-full uppercase tracking-wider';
const lockedBadgeClass =
    'text-[9px] font-semibold bg-surface-sunken text-fg-tertiary px-1 py-px rounded-full uppercase tracking-wider';

// BAR-1351: `mcpOnly` is REQUIRED, not optional with a default. A call site
// that forgets it fails the build, which is the only way to stop a third
// locked card shipping later with the wrong copy. For an MCP-only customer the
// card opens the connect page (`onOpen` is the caller's choice), so its name
// says that instead of "access details".
function LockedModuleCard({ moduleName, onOpen, mcpOnly }: { moduleName: string; onOpen: () => void; mcpOnly: boolean }) {
    return (
        <button
            type="button"
            onClick={onOpen}
            aria-label={mcpOnly ? 'See how to connect BARK to your AI assistant' : `View ${moduleName} access details`}
            className="w-full text-left rounded-xl border border-border-default bg-bg-warm px-5 py-5 flex items-start gap-3
                       hover:bg-surface-sunken transition-colors focus-visible:outline-none focus-visible:shadow-bark-focus"
        >
            <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md bg-surface-sunken text-fg-tertiary">
                <Lock className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
            </span>
            <div>
                <p className="font-body text-[13px] font-semibold leading-relaxed text-fg-secondary">
                    {mcpOnly ? 'Not part of your plan' : 'Not enabled for your organization'}
                </p>
                <p className="mt-1 font-body text-[12px] leading-relaxed text-fg-tertiary">
                    {mcpOnly
                        ? 'Your BARK plan works inside Claude, ChatGPT and Codex. See how to connect BARK.'
                        : 'View access details or contact your BARK representative.'}
                </p>
            </div>
        </button>
    );
}

// Placeholder card for modules whose product surface is not built yet
// (BAR-538 phase 3 fills these in). Non-interactive by design.
function ModulePlaceholderCard({ name, blurb, icon }: { name: string; blurb: string; icon: React.ReactNode }) {
    return (
        <section aria-label={`${name} module (coming soon)`} className="flex flex-col h-full">
            <div className="flex items-baseline justify-between mb-3">
                <span className="font-body text-[20px] font-semibold text-fg-tertiary leading-tight flex items-center gap-2">
                    {name}
                    <span className={soonBadgeClass}>soon</span>
                </span>
            </div>
            <div className="flex-1 rounded-xl bg-bg-warm shadow-bark-sm px-5 py-5 flex items-start gap-3">
                <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md bg-surface text-fg-tertiary">
                    {icon}
                </span>
                <p className="font-body text-[13px] leading-relaxed text-fg-tertiary max-w-[420px] pt-0.5">
                    {blurb}
                </p>
            </div>
        </section>
    );
}

// Live-looking teaser card (no "soon" mark) — used by Reporting and
// Inventory so both read as offered products rather than absent ones.
function ModuleTeaserCard({ name, blurb, icon, actionLabel, onAction }: {
    name: string; blurb: string; icon: React.ReactNode;
    actionLabel?: string; onAction?: () => void;
}) {
    const headingId = `module-${name.toLowerCase().replace(/\s+/g, '-')}-heading`;
    return (
        <section aria-labelledby={headingId} className="flex flex-col h-full">
            <div className="flex items-baseline justify-between mb-3">
                <span id={headingId} className="font-body text-[20px] font-semibold text-fg leading-tight">
                    {name}
                </span>
                {onAction && actionLabel && (
                    <button type="button" onClick={onAction} className={openLinkClass}>
                        <span>{actionLabel}</span>
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
                    </button>
                )}
            </div>
            {onAction ? (
                <button
                    type="button"
                    onClick={onAction}
                    className="flex-1 w-full text-left rounded-xl bg-bg-warm shadow-bark-sm px-5 py-5 flex items-start gap-3
                               hover:shadow-bark-md hover:bg-surface-sunken transition-all cursor-pointer"
                >
                    <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md bg-surface text-fg">
                        {icon}
                    </span>
                    <p className="font-body text-[13px] leading-relaxed text-fg-secondary max-w-[420px] pt-0.5">
                        {blurb}
                    </p>
                </button>
            ) : (
                <div className="flex-1 rounded-xl bg-bg-warm shadow-bark-sm px-5 py-5 flex items-start gap-3">
                    <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md bg-surface text-fg">
                        {icon}
                    </span>
                    <p className="font-body text-[13px] leading-relaxed text-fg-secondary max-w-[420px] pt-0.5">
                        {blurb}
                    </p>
                </div>
            )}
        </section>
    );
}

/** `tutorials={false}` leaves out the Research / Build how-to cards and shows
 *  only the product row (Reporting, Inventory and the two "soon" cards) —
 *  the start page's use since 2026-10-17. */
export default function HomeModuleCards({ tutorials = true }: { tutorials?: boolean } = {}) {
    const { beginBlankDraft, onOpenUnlockModule, navigateToView } = useShellUiActions();
    const { isModuleDisabled } = useAuthActions();
    // BAR-1351: an MCP-only customer has no representative to contact and no
    // web plan to unlock. Same predicate the sidebar uses, from the same
    // context, so the two surfaces cannot disagree.
    const mcpOnlyCustomer = isMcpOnlyCustomer(isModuleDisabled);
    // The locked card of an MCP-only customer opens the page that connects
    // their assistant; everyone else gets the access-details dialog.
    const openLockedCard = (label: string) => (mcpOnlyCustomer
        ? navigateToView('connect')
        : onOpenUnlockModule(label));
    const researchLocked = isModuleDisabled('regulatory_research');
    const buildLocked = isModuleDisabled('build_artifact');
    const reportingLocked = isModuleDisabled('reporting');

    return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-6 lg:gap-x-8 gap-y-10 lg:gap-y-12">
            {tutorials && (<>
            {/* Research card */}
            <section aria-labelledby="module-research-heading">
                <div className="flex items-baseline justify-between mb-3">
                    <h2
                        id="module-research-heading"
                        className={`font-body text-[20px] font-semibold leading-tight flex items-center gap-2 ${
                            researchLocked ? 'text-fg-tertiary' : 'text-fg'
                        }`}
                    >
                        Research
                        {researchLocked && (
                            <>
                                <span aria-hidden="true" className={lockedBadgeClass}>Locked</span>
                                <span className="sr-only"> — locked</span>
                            </>
                        )}
                    </h2>
                    <button
                        type="button"
                        onClick={() => researchLocked
                            ? onOpenUnlockModule('Regulatory research')
                            : beginBlankDraft('research')}
                        className={researchLocked ? lockedLinkClass : openLinkClass}
                    >
                        <span>{researchLocked ? 'View research access' : 'Open research workspace'}</span>
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
                    </button>
                </div>
                {researchLocked
                    ? (
                        <LockedModuleCard
                            moduleName="research"
                            onOpen={() => openLockedCard('Regulatory research')}
                            mcpOnly={mcpOnlyCustomer}
                        />
                    )
                    : <ModuleTutorialCard steps={RESEARCH_TUTORIAL_STEPS} variant="research" />}
            </section>

            {/* Build remains visible when unavailable so the gate reads as an
                intentional account entitlement, not a missing feature. */}
            <section aria-labelledby="module-build-heading">
                <div className="flex items-baseline justify-between mb-3">
                    <h2
                        id="module-build-heading"
                        className={`font-body text-[20px] font-semibold leading-tight flex items-center gap-2 ${
                            buildLocked ? 'text-fg-tertiary' : 'text-fg'
                        }`}
                    >
                        Build
                        {buildLocked && (
                            <>
                                <span aria-hidden="true" className={lockedBadgeClass}>Locked</span>
                                <span className="sr-only"> — locked</span>
                            </>
                        )}
                    </h2>
                    <button
                        type="button"
                        onClick={() => buildLocked
                            ? onOpenUnlockModule('Build')
                            : beginBlankDraft('build')}
                        className={buildLocked ? lockedLinkClass : openLinkClass}
                    >
                        <span>{buildLocked ? 'View build access' : 'Open build workspace'}</span>
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
                    </button>
                </div>
                {buildLocked
                    ? (
                        <LockedModuleCard
                            moduleName="build"
                            onOpen={() => openLockedCard('Build')}
                            mcpOnly={mcpOnlyCustomer}
                        />
                    )
                    : <ModuleTutorialCard steps={BUILD_TUTORIAL_STEPS} variant="build" />}
            </section>
            </>)}

            {/* Second row of modules: Reporting + Inventory teasers
                (unlockable add-ons) + two "soon" placeholders — one row of 4
                on xl, 2x2 on md. Regulatory monitoring has NO card here on
                purpose: it gets its own preview section further down the page
                (next to the regulator scan), and a card would say the same
                thing twice (removed 2026-07-13 on Kawi's call). */}
            <div className="xl:col-span-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-6 lg:gap-x-8 gap-y-10 lg:gap-y-12">
                <ModuleTeaserCard
                    name="Reporting"
                    blurb="One unified data model feeds your COREP, FINREP, forecasts, scenarios and models. Each figure traces back to its data and to the article that requires it."
                    icon={<FileBarChart className="w-5 h-5" strokeWidth={1.5} />}
                    actionLabel="Open reporting"
                    onAction={() => reportingLocked
                        ? onOpenUnlockModule('Reporting')
                        : navigateToView('reporting-preview')}
                />
                <ModuleTeaserCard
                    name="Inventory"
                    blurb="The GitHub for banks. Keep every model, document and obligation in one living register, versioned and ready for inspection."
                    icon={<Boxes className="w-5 h-5" strokeWidth={1.5} />}
                    actionLabel="Open inventory"
                    onAction={() => onOpenUnlockModule('Inventory')}
                />
                <ModulePlaceholderCard
                    name="Planning & stress testing"
                    blurb="Forecast capital and liquidity under any scenario you can name, from the same model that feeds your regulatory reporting. ICAAP and ILAAP become a review, not a project."
                    icon={<Activity className="w-5 h-5" strokeWidth={1.5} />}
                />
                <ModulePlaceholderCard
                    name="Limit monitoring"
                    blurb="Limits live in the same register as your obligations, models and documents. Monitoring builds on what you already keep in your inventory."
                    icon={<Gauge className="w-5 h-5" strokeWidth={1.5} />}
                />
            </div>
        </div>
    );
}
